import { action } from '@ember/object';
import type { AsyncBelongsTo } from '@ember-data/model';
import Component from '@glimmer/component';
import { sortBy } from '@nullvoxpopuli/ember-composable-helpers';
import type { DetailedChangeset } from 'ember-changeset/types';
import { and } from 'ember-truth-helpers';
import EntriesDateField from 'garaje/components/entries/date-field';
import EntriesInputField from 'garaje/components/entries/input-field';
import UiInputField from 'garaje/components/ui-input-field';
import featureEnabled from 'garaje/helpers/feature-enabled';
import removedString from 'garaje/helpers/removed-string';
import type EntryModel from 'garaje/models/entry';
import type InviteModel from 'garaje/models/invite';
import type SignInFieldModel from 'garaje/models/sign-in-field';
import type UserModel from 'garaje/models/user';
import italicizeIfRemoved from 'garaje/modifiers/italicize-if-removed';

interface EntriesNotAssignableFlowFieldsSignature {
  Args: {
    model: EntryModel | InviteModel;
    userDataChangeset: Array<DetailedChangeset<SignInFieldModel>>;
    signInFields?: SignInFieldModel[];
    handleUpdate: () => void;
  };
}

export default class EntriesNotAssignableFlowFields extends Component<EntriesNotAssignableFlowFieldsSignature> {
  get mappedSignInFields(): Record<string, unknown> {
    return (this.args.signInFields ?? []).reduce<Record<string, unknown>>((map, field) => {
      map[field.name] = field.value;
      return map;
    }, {});
  }

  get renderableFields(): Array<DetailedChangeset<SignInFieldModel>> {
    const mappedSignInFields = this.mappedSignInFields;

    return this.args.userDataChangeset.filter(
      (fieldChangeset) =>
        <boolean>(<unknown>fieldChangeset.isLoaded) &&
        (fieldChangeset.isCustom || mappedSignInFields[fieldChangeset.name]),
    );
  }

  get creator(): AsyncBelongsTo<UserModel> | undefined {
    return 'creator' in this.args.model ? this.args.model.creator : undefined;
  }

  @action
  onUpdate(): void {
    if (this.args.handleUpdate) {
      this.args.handleUpdate();
    }
  }

  <template>
    <div data-test-screening-field="fullName">
      <label class="cursor-default">
        Full name
      </label>
      <input
        type="text"
        value={{removedString @model.fullName}}
        {{italicizeIfRemoved @model.fullName}}
        disabled="disabled"
        class="readonly"
        readonly="readonly"
      />
    </div>

    {{#if @model.email}}
      <div data-test-screening-field="email">
        <label class="cursor-default">
          Email address
        </label>
        <input
          type="text"
          value={{removedString @model.email}}
          {{italicizeIfRemoved @model.email}}
          disabled="disabled"
          class="readonly"
          readonly="readonly"
        />
      </div>
    {{/if}}

    {{#unless @model.isFromWalkUp}}
      {{#each (sortBy "position" this.renderableFields) as |field|}}
        {{#if (and field.isDate (featureEnabled "date-sign-in-field"))}}
          <EntriesDateField
            @disabled={{if @model.anonymizedAt true false}}
            @changeset={{field}}
            @update={{this.onUpdate}}
          />
        {{else}}
          <EntriesInputField
            @disabled={{if @model.anonymizedAt true false}}
            @changeset={{field}}
            @update={{this.onUpdate}}
          />
        {{/if}}
      {{/each}}
    {{/unless}}

    {{#if @model.propertyNotes}}
      <UiInputField data-test-shared-notes-field class="flex-auto min-w-0 min-h-0 items-center" as |Field|>
        <Field.label for="shared-notes-textarea">Shared notes</Field.label>
        <textarea
          id="shared-notes-textarea"
          disabled="disabled"
          value={{@model.propertyNotes}}
          rows={{5}}
          class="resize-none"
          data-test-shared-notes-textarea
        >
        </textarea>
      </UiInputField>
    {{/if}}

    {{#if (and this.creator.content.id @model.isFromWalkUp)}}
      <div data-test-creator-field>
        <label for="creator-field">Created By</label>
        <input
          id="creator-field"
          data-test-creator-field-input
          disabled
          type="text"
          value={{this.creator.content.fullName}}
        />
      </div>
    {{/if}}
  </template>
}
